<template>
  <div class="user-external-notifications">
    <div class="panel-header" v-if="!hideHeader">{{ pageTitle }}</div>
    <div class="panel-body">
      <div class="info-message" v-if="loadingConfig">
        <i class="fa fa-spinner fa-spin mr-1"></i>Loading
      </div>
      <div class="info-message" v-else-if="!userId">
        <i class="fa fa-exclamation-triangle mr-1"></i>User info not provided
      </div>
      <template v-else-if="userNotificationsConfig">
        <div class="alert alert-danger" v-if="errorMessages.length > 0">
          <div v-for="(message, index) of errorMessages" :key="`error_message_${index}`">*{{ message }}</div>
        </div>
        <div class="notification-sections-wrapper">
          <div class="notification-section">
            <div class="notification-section-header">Recipient Info</div>
            <div class="notification-info-wrapper">
              <div class="mb-2">Please provide a phone number and/or email address where you'd like the notifications to be delivered</div>
              <div class="notification-info-title">Phone Number</div>
              <input type="text" class="form-control-sm btn-block mb-2" placeholder="Phone Number" v-model="userNotificationsConfig.NotificationPhone" />
              <div class="notification-info-title">Email Address</div>
              <input type="text" class="form-control-sm btn-block mb-2" placeholder="Email Address" v-model="userNotificationsConfig.NotificationEmail" />
              <span class="font-red" v-if="infoMessage">
                {{infoMessage}}
              </span>
            </div>
          </div>
          <div class="notification-section">
            <div class="notification-section-header">Notifications</div>
            <div class="notification-options-wrapper">
              <div class="mb-2">Please configure notifications by type</div>
              <table class="notification-options-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th class="text-center">Email</th>
                    <th class="text-center">SMS</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(notificationGroup, index) of userNotificationsConfig.NotificationGroups" :key="`user_external_notification_${notificationGroup.GroupType}_${index}`">
                    <td>{{ notificationGroup.GroupTypeName }}</td>
                    <td class="text-center">
                      <i class="font-icon fa" :class="notificationGroup.NotifyViaEmail ? 'fa-check-square font-blue' : 'fa-square-o'" @click="notificationGroup.NotifyViaEmail = !notificationGroup.NotifyViaEmail"></i>
                    </td>
                    <td class="text-center">
                      <i class="font-icon fa" :class="notificationGroup.NotifyViaPhone ? 'fa-check-square font-blue' : 'fa-square-o'" @click="notificationGroup.NotifyViaPhone = !notificationGroup.NotifyViaPhone"></i>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="notification-footer">
          <button type="button" class="btn btn-sm btn-outline mr-3" @click="resetConfig" :disabled="savingConfig">Reset</button>
          <button type="button" class="btn btn-sm mr-0" @click="saveConfig" :disabled="savingConfig"><i class="fa fa-spinner fa-spin mr-1" v-if="savingConfig"></i>Save</button>
        </div>
      </template>
      <div class="info-message" v-else>
        <i class="fa fa-exclamation-triangle mr-1"></i>Error loading user notification preferences. Please try again later.
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { GroupType } from "@/lib/utilities/enums.js";

export default {
  name: "userExternalNotifications",
  computed: {
    ...mapGetters(["User"]),
    userId() {
      // If a specific userId is passed in the url, use that
      if (this.$route && this.$route.query && this.$route.query.userId)
        return this.$route.query.userId;

      // Else, default to logged in user (users clicking on aalnk)
      return this.User.UserId;
    },
    hideHeader() {
      return (
        this.$route && this.$route.query && this.$route.query.header == "false"
      );
    },
    pageTitle() {
      if (this.userInfo && this.userInfo.UserId)
        return `Manage Notifications for ${this.userInfo.Name}`;
      return `Manage Notifications`;
    },
  },
  data() {
    return {
      userInfo: null,
      savedConfig: null,
      userNotificationsConfig: null,
      loadingConfig: true,
      savingConfig: false,
      errorMessages: [],
      infoMessage: null,
    };
  },
  watch: {
    userInfo() {
      this.infoMessage = null;
      if (!this.userNotificationsConfig) return;

      let noPhone = false,
        noEmail = false;

      if (
        !this.userNotificationsConfig.NotificationPhone &&
        this.userInfo &&
        this.userInfo.MobileNumber
      ) {
        this.userNotificationsConfig.NotificationPhone =
          this.userInfo.MobileNumber;
        noPhone = true;
      }

      if (
        !this.userNotificationsConfig.NotificationEmail &&
        this.userInfo &&
        this.userInfo.Email
      ) {
        this.userNotificationsConfig.NotificationEmail = this.userInfo.Email;
        noEmail = true;
      }

      if (noPhone && noEmail)
        this.infoMessage =
          "Phone and email are missing from your notification settings and are copied from your user profile. Please click Save to update.";
      else if (noPhone)
        this.infoMessage =
          "Phone is missing from your notification settings and is copied from your user profile. Please click Save to update.";
      else if(noEmail)
        this.infoMessage =
          "Email is missing from your notification settings and is copied from your user profile. Please click Save to update.";
    },
  },
  mounted() {
    this.getUserInfo();
    this.getConfig();
  },
  methods: {
    async getUserInfo() {
      if (!this.userId) return;

      const response = await this.$store.dispatch("getUserInfo", this.userId);
      this.userInfo =
        response && response.data && response.data.Success
          ? response.data.Data
          : null;
    },
    async getConfig() {
      if (!this.userId) {
        this.loadingConfig = false;
        return;
      }

      this.userNotificationsConfig = null;
      this.loadingConfig = true;
      const response = await this.$store.dispatch(
        "getUserExternalNotifications",
        this.userId
      );
      this.loadingConfig = false;

      if (!response || !response.data || !response.data.Success)
        this.$snotify.error(
          "There was an error loading user notification preferences. Please try again later."
        );
      else this.processSavedConfig(response.data.Data);
    },
    processSavedConfig(data) {
      if (!data)
        data = {
          NotificationUserId: this.userId,
          NotificationPhone: null,
          NotificationEmail: null,
          NotificationGroups: [],
        };

      const supportedGroupTypes = [
        GroupType.CustomerLead,
        GroupType.LeadClaim,
        GroupType.CustomerEmail,
        GroupType.CustomerSalesSMS,
        GroupType.CustomerServiceSMS,
        GroupType.LeadGroup,
        GroupType.WorkflowTaskNotification,
        GroupType.PAL,
      ];

      if (!data.NotificationGroups) data.NotificationGroups = [];
      supportedGroupTypes.forEach((grpType) => {
        // Check if any supported group type is missing in config
        if (!data.NotificationGroups.some((ng) => ng.GroupType == grpType)) {
          // Add missing group type to config
          data.NotificationGroups.push({
            UserId: data.NotificationUserId,
            GroupType: grpType,
            NotifyViaEmail: false,
            NotifyViaPhone: false,
          });
        }
      });

      // Set name for each group type
      data.NotificationGroups.forEach((group) => {
        group.GroupTypeName = this.getGroupTypeName(group.GroupType);
      });

      // Sort by group type name
      data.NotificationGroups.sort((group1, group2) =>
        group1.GroupTypeName > group2.GroupTypeName ? 1 : -1
      );

      this.savedConfig = data;
      this.userNotificationsConfig = JSON.parse(JSON.stringify(data));
    },
    getGroupTypeName(groupType) {
      switch (groupType) {
        case GroupType.CustomerLead:
          return "New Lead";
        case GroupType.LeadClaim:
          return "Claimable Lead";
        case GroupType.CustomerEmail:
          return "Incoming Customer Email";
        case GroupType.CustomerSalesSMS:
          return "Incoming Customer SMS (Sales)";
        case GroupType.CustomerServiceSMS:
          return "Incoming Customer SMS (Service)";
        case GroupType.LeadGroup:
          return "Workflow Task";
        case GroupType.WorkflowTaskNotification:
          return "Workflow Task Notification";
        case GroupType.PAL:
          return "PAL Messages";
        default:
          return "Unknown";
      }
    },
    resetConfig() {
      this.errorMessages = [];
      this.processSavedConfig(this.savedConfig);
    },
    async saveConfig() {
      this.errorMessages = [];

      var phoneReg = /^\D?(\d{3})\D?\D?(\d{3})\D?(\d{4})$/;
      var emailReg =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

      this.userNotificationsConfig.NotificationPhone = (
        this.userNotificationsConfig.NotificationPhone || ""
      ).trim();
      this.userNotificationsConfig.NotificationEmail = (
        this.userNotificationsConfig.NotificationEmail || ""
      ).trim();

      const notifyViaPhoneSelected =
        this.userNotificationsConfig.NotificationGroups.some(
          (ng) => ng.NotifyViaPhone == true
        );
      const notifyViaEmailSelected =
        this.userNotificationsConfig.NotificationGroups.some(
          (ng) => ng.NotifyViaEmail == true
        );

      if (
        notifyViaPhoneSelected &&
        this.userNotificationsConfig.NotificationPhone.length == 0
      )
        this.errorMessages.push("Please enter a phone number");
      if (
        notifyViaEmailSelected &&
        this.userNotificationsConfig.NotificationEmail.length == 0
      )
        this.errorMessages.push("Please enter an email address");

      if (this.errorMessages.length > 0) return;

      if (
        this.userNotificationsConfig.NotificationPhone.length > 0 &&
        !phoneReg.test(this.userNotificationsConfig.NotificationPhone)
      )
        this.errorMessages.push("Invalid recipient phone number");
      if (
        this.userNotificationsConfig.NotificationEmail.length > 0 &&
        !emailReg.test(this.userNotificationsConfig.NotificationEmail)
      )
        this.errorMessages.push("Invalid recipient email address");

      if (this.errorMessages.length > 0) return;

      const request = {
        userId: this.userId,
        payload: this.userNotificationsConfig,
      };

      this.savingConfig = true;
      const response = await this.$store.dispatch(
        "saveUserExternalNotifications",
        request
      );
      this.savingConfig = false;

      if (!response || !response.data || !response.data.Success)
        this.$snotify.error(
          "There was an error saving user notification preferences. Please try again later."
        );
      else {
        this.$snotify.success(
          "Successfully saved user notification preferences"
        );
        this.processSavedConfig(response.data.Data);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.user-external-notifications {
  width: 100%;
  height: 100%;
  display: flex;
  flex-flow: column wrap;

  .panel-header {
    height: 60px;
    overflow: hidden;
    padding: 15px 15px;
  }

  .panel-body {
    flex: 1;
    overflow: auto;
    padding: 15px 15px;

    @media screen and (max-width: 800px) {
      .notification-sections-wrapper {
        display: unset !important;
      }
    }

    .info-message {
      padding-bottom: 15px;
      font-size: 16px;
      line-height: 30px;
    }

    .alert {
      max-width: 1000px;
      margin: 0px auto;
      margin-bottom: 15px;
    }

    .notification-sections-wrapper {
      display: flex;
      flex-flow: row wrap;
      gap: 15px;
      margin: 0px auto;

      .notification-section {
        flex: 1;
        border: 1px solid $gray-lt;
        border-radius: 10px;
        margin-bottom: 15px;

        .notification-section-header {
          font-size: 18px;
          line-height: 40px;
          padding: 0px 10px;
          margin-bottom: 10px;
          border-bottom: 1px solid $gray-lt;
        }

        .notification-info-wrapper,
        .notification-options-wrapper {
          margin: 0px 10px 10px;
        }

        .notification-options-table {
          width: 100%;

          thead {
            tr {
              border-bottom: 1px solid $gray-lt;

              th {
                padding: 5px;
                line-height: 20px;
              }
            }
          }

          tbody {
            tr {
              border-bottom: 1px solid $gray-lt;

              &:last-child {
                border-bottom-width: 0px;
              }

              td {
                padding: 5px;
                line-height: 25px;

                .font-icon {
                  font-size: 18px;
                  line-height: 25px;
                  vertical-align: top;
                  cursor: pointer;
                }
              }
            }
          }
        }
      }
    }

    .notification-footer {
      padding: 15px;
      text-align: center;
    }
  }
}
</style>


// WEBPACK FOOTER //
// src/components/pages/Users/UserExternalNotifications.vue